import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { PublicClientApplication, AuthenticationResult, AccountInfo } from '@azure/msal-browser';
import { msalConfig, loginRequest } from '../config/authConfig';

// User interface with extended properties from Microsoft Graph API
export interface User {
  id: string;
  displayName: string;
  email: string;
  roles: string[];
  employeeId?: number;
  profileImage?: string;
  accessToken?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  error: Error | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  acquireToken: () => Promise<string | null>;
}

// Create context with default values
export const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isInitializing: true,
  error: null,
  login: async () => {},
  logout: async () => {},
  acquireToken: async () => null,
});

interface AuthProviderProps {
  children: ReactNode;
}

// Check if we're in mock auth mode
const useMockAuth = process.env.REACT_APP_USE_MOCK_AUTH === 'true';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Initialize MSAL instance
  const msalInstance = useMockAuth ? null : new PublicClientApplication(msalConfig);

  // Process user data from Microsoft authentication
  const processAuthResult = (result: AuthenticationResult): User => {
    const account = result.account;
    const idTokenClaims = account?.idTokenClaims as { [key: string]: any } || {};
    
    // Extract roles from token claims or default to empty array
    const roles = idTokenClaims.roles || [];
    
    return {
      id: account?.homeAccountId || '',
      displayName: account?.name || '',
      email: account?.username || '',
      roles,
      accessToken: result.accessToken,
    };
  };

  // Handle mock authentication
  const handleMockAuth = async (action: 'login' | 'logout' = 'login') => {
    if (action === 'logout') {
      localStorage.removeItem('mockUser');
      setUser(null);
      setIsAuthenticated(false);
      return;
    }

    // Create mock user with admin role
    const mockUser: User = {
      id: 'mock-user-id',
      displayName: 'Mock Admin',
      email: 'admin@example.com',
      roles: ['admin', 'hr', 'payroll'],
      employeeId: 1,
    };

    // Store in local storage for persistence
    localStorage.setItem('mockUser', JSON.stringify(mockUser));
    setUser(mockUser);
    setIsAuthenticated(true);
  };

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsInitializing(true);
        
        if (useMockAuth) {
          // Check for stored mock user
          const storedUser = localStorage.getItem('mockUser');
          if (storedUser) {
            setUser(JSON.parse(storedUser));
            setIsAuthenticated(true);
          }
        } else if (msalInstance) {
          // Check for logged in accounts
          const accounts = msalInstance.getAllAccounts();
          
          if (accounts.length > 0) {
            msalInstance.setActiveAccount(accounts[0]);
            
            // Silently acquire token
            const result = await msalInstance.acquireTokenSilent({
              scopes: loginRequest.scopes,
              account: accounts[0],
            });
            
            const userData = processAuthResult(result);
            setUser(userData);
            setIsAuthenticated(true);
          }
        }
      } catch (err) {
        // Only log critical errors
        setError(err as Error);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async () => {
    try {
      setError(null);
      
      if (useMockAuth) {
        await handleMockAuth('login');
        return;
      }
      
      if (!msalInstance) {
        throw new Error('MSAL instance not initialized');
      }
      
      const result = await msalInstance.loginPopup({
        scopes: loginRequest.scopes,
        prompt: 'select_account',
      });
      
      const userData = processAuthResult(result);
      setUser(userData);
      setIsAuthenticated(true);
      
      // Get additional user data from backend
      await fetchUserProfile(userData.accessToken!);
      
    } catch (err) {
      // Silently handle login errors
      setError(err as Error);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      if (useMockAuth) {
        await handleMockAuth('logout');
        return;
      }
      
      if (!msalInstance) {
        throw new Error('MSAL instance not initialized');
      }
      
      await msalInstance.logoutPopup();
      setUser(null);
      setIsAuthenticated(false);
    } catch (err) {
      // Silently handle logout errors
      setError(err as Error);
    }
  };

  // Acquire token function for API calls
  const acquireToken = async (): Promise<string | null> => {
    try {
      if (useMockAuth) {
        return 'mock-access-token';
      }
      
      if (!msalInstance) {
        throw new Error('MSAL instance not initialized');
      }
      
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        return null;
      }
      
      const result = await msalInstance.acquireTokenSilent({
        scopes: loginRequest.scopes,
        account: accounts[0],
      });
      
      return result.accessToken;
    } catch (err) {
      // Silently handle token acquisition errors
      setError(err as Error);
      return null;
    }
  };

  // Fetch additional user profile data from our backend
  const fetchUserProfile = async (accessToken: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch user profile');
      }
      
      const profileData = await response.json();
      
      // Update user data with additional profile info
      setUser(currentUser => ({
        ...currentUser!,
        employeeId: profileData.employeeId,
        profileImage: profileData.profileImage
      }));
    } catch (err) {
      // Silently handle profile fetch errors
    }
  };

  const contextValue: AuthContextType = {
    user,
    isAuthenticated,
    isInitializing,
    error,
    login,
    logout,
    acquireToken,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
